import { TimeFormatOptions } from '@griply/types';

import { reminderMinuteOptions } from './calendar-event-reminders';
import { formatTimeOfDay } from './format-time-of-day';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 60 * 24;
const DAYS_PER_WEEK = 7;
const MS_PER_MINUTE = 60 * 1000;

export type CalendarEventReminderLabel = {
  key: string;
  count?: number;
  time?: string;
};

export type GetCalendarEventReminderLabelOptions = {
  allDay?: boolean;
  timeFormat?: TimeFormatOptions;
};

// An all-day event starts at midnight, so "1 week before at 09:00" reaches us as 9540.
const getAllDayLabel = (
  minutes: number,
  timeFormat?: TimeFormatOptions,
): CalendarEventReminderLabel => {
  const daysBefore = Math.ceil(minutes / MINUTES_PER_DAY);
  const time =
    formatTimeOfDay(
      (daysBefore * MINUTES_PER_DAY - minutes) * MS_PER_MINUTE,
      timeFormat,
    ) ?? '';

  if (daysBefore % DAYS_PER_WEEK === 0) {
    return {
      key: 'forms.event.fields.reminders.weeks-before-at',
      count: daysBefore / DAYS_PER_WEEK,
      time,
    };
  }

  return {
    key: 'forms.event.fields.reminders.days-before-at',
    count: daysBefore,
    time,
  };
};

// A descriptor rather than a string, so this stays free of an i18n instance.
export const getCalendarEventReminderLabel = (
  minutes: number,
  { allDay, timeFormat }: GetCalendarEventReminderLabelOptions = {},
): CalendarEventReminderLabel => {
  // Presets keep preset wording; a foreign offset (Google "1 day before at 09:00" = 900) is day-anchored.
  const isDayAnchored =
    minutes >= MINUTES_PER_DAY ||
    (minutes > 0 && !reminderMinuteOptions.includes(minutes));

  if (allDay && isDayAnchored) {
    return getAllDayLabel(minutes, timeFormat);
  }

  if (minutes === 0) {
    return { key: 'forms.event.fields.reminders.at-time-of-event' };
  }

  if (minutes % MINUTES_PER_DAY === 0) {
    return {
      key: 'forms.event.fields.reminders.days-before',
      count: minutes / MINUTES_PER_DAY,
    };
  }

  if (minutes % MINUTES_PER_HOUR === 0) {
    return {
      key: 'forms.event.fields.reminders.hours-before',
      count: minutes / MINUTES_PER_HOUR,
    };
  }

  return { key: 'forms.event.fields.reminders.minutes-before', count: minutes };
};
